import { isFulfilled } from "@reduxjs/toolkit";
import { useFormik } from "formik";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { toast } from "react-toastify";
import { DEFAULT_ITEMS_PER_PAGE, DEFAULT_SEARCH_INPUT_DEBOUNCE_DELAY, DEFAULT_SEARCH_INPUT_LENGTH } from "../../constants/app";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import Roles from "../../models/roles";
import { AUTH_ROUTE } from "../../pages/App/routes";
import {
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
} from "../../store";
import {
  hideTransparentPreloaderAction,
  showNotificationAction,
  showTransparentPreloaderAction,
} from "../../store/application/slice";
import { getAddressSuggestionsThunk } from "../../store/auth/thunks";
import { AddressSuggestion } from "../../store/auth/types";
import {
  getAllCenters,
  getAllCentreUsers,
  updateCentreDetails,
} from "../../store/centers/thunks";
import {
  CentreDetailsPayload,
  TAllCentersList,
} from "../../store/centers/types";
import {
  createNewCentreThunk,
  getCentreDetailsThunk,
  updateCentreDetailsThunk,
} from "../../store/superadmin/thunks";
import { TCenterDetails } from "../../store/superadmin/types";
import { createValidationSchema } from "../../utils/createValidationSchema";
import CustomButton from "../UI/Buttons/CustomButton";
import CircleTick from "../UI/Icons/CircleTick";
import LeftArrow from "../UI/Icons/LeftArrow";
import RoundedPlus from "../UI/Icons/RoundedPlus";
import Warning from "../UI/Icons/Warning";
import Checkbox from "../UI/Inputs/Checkbox";
import ImageUpload from "../UI/Inputs/ImageUpload";
import TextArea from "../UI/Inputs/TextArea";
import TextInput from "../UI/Inputs/TextInput";
import ErrorMessage from "../UI/Tooltips/ErrorMessage";

interface Transport {
  mode: string;
  parking_on_site?: number;
  parking_on_surrounding?: number;
}

const hoursOptions: { key: string; value: string }[] = [];
for (let hour = 0; hour < 24; hour++) {
  const formattedHour = hour % 12 === 0 ? 12 : hour % 12;
  const period = hour < 12 ? "AM" : "PM";
  const key = hour.toString().padStart(2, "0");
  hoursOptions.push({
    key: `${key}:00`,
    value: `${formattedHour}:00${period}`,
  });
  hoursOptions.push({
    key: `${key}:30`,
    value: `${formattedHour}:30${period}`,
  });
}

const CreateCentres: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const error = useSelector((state) => state.auth.error);
  const superAdminError = useSelector((state) => state.superadmin.error);
  const allCentersList = useSelector((state) => state.Centres.allCenters);
  const inProgress = useSelector((state) => state.Centres.inProgress);
  const superAdminInProgress = useSelector(
    (state) => state.superadmin.inProgress
  );
  const profileImagePath = useSelector(
    (state) => state.superadmin.centrePicImage?.photo_path
  );
  const centreProfileImagePath = useSelector(
    (state) => state.Centres.centrePicImagePath
  );
  const centreDetails = useSelector((state) => state.superadmin.centreDetails);
  const totalCentreUserCount = useSelector(
    (state) => state.Centres.totalCentreUserCount
  );
  const user = useSelector((state) => state.auth.user);
  const isShowNotification = useSelector(
    (state) => state.app.isShowNotification
  );
  const addressSuggestionArr = useSelector(
    (state) => state.auth.addressSuggestions
  );

  const isAdmin = useMemo(
    () =>
      user?.role.name === Roles.SUPER_ADMIN ||
      user?.role.name === Roles.GENERAL_ADMIN,
    [user]
  );
  const isCentreAdmin = useMemo(
    () => user?.role.name === Roles.CENTER_ADMIN,
    [user]
  );
  const isCentreUser = useMemo(
    () => user?.role.name === Roles.CENTER_USER,
    [user]
  );

  const [uploadValidationError, setUploadValidationError] = useState<
    string | null
  >(null);
  const [currentCentreId, setCurrentCentreId] = useState<number | null>(null);
  const [transport, setTransport] = useState<
    Array<{
      mode: string;
      parking_on_site?: number;
      parking_on_surrounding?: number;
    }>
  >([]);
  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [showBanner, setShowBanner] = useState<boolean>(false);
  const [showNotification, setShowNotification] = useState<boolean>(false);
  const isCenterLoaded = useRef<boolean>(true);
  const [searchInput, setSearchInput] = useState<string>("");
  const [selectedKeyword, setSelectedKeyword] = useState<string>("");
  const [addressSuggestions, setAddressSuggestions] = useState<
    AddressSuggestion[]
  >([]);
  const [hasAddressSelected, setHasAddressSelected] = useState<boolean>(false)

  // useEffect(() => {
  //   if (addressSuggestionArr.length > 0) {
  //     setAddressSuggestions(addressSuggestionArr);
  //   }
  // }, [addressSuggestionArr]);

  /**
   * Debounces the `selectedKeyword` state to avoid excessive API calls while the user is typing.
   * The debounced value is used to trigger the API call for fetching candidates.
   */
  const debouncedSearch = useDebouncedValue(
    searchInput,
    DEFAULT_SEARCH_INPUT_DEBOUNCE_DELAY
  );

  /**
   * Handles the change in the search input by updating the `selectedKeyword` state.
   * It also resets the current page to 1 if the search input is empty or exceeds the default length.
   *
   */
  useEffect(() => {
    if (
      debouncedSearch.length === 0 ||
      debouncedSearch.length > DEFAULT_SEARCH_INPUT_LENGTH
    ) {
      setSelectedKeyword(debouncedSearch)
    }
  }, [debouncedSearch]);

  // useEffect(() => {
  //   if(selectedKeyword !== ""){
  //     const handleAddressSuggestions = async(selectedKeyword: string) => {
  //       await dispatch(getAddressSuggestionsThunk(selectedKeyword));
  //     }

  //     handleAddressSuggestions(selectedKeyword)
  //   } else {
  //     setAddressSuggestions([])
  //   }
  // }, [dispatch, selectedKeyword])

  const daysOfWeek = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  useEffect(() => {
    if (location.pathname.includes("create")) {
      setShowBanner(true);
      sessionStorage.clear();
      setCurrentCentreId(null);
    }
  }, [location]);
  /**
   * Handles the change event for a checkbox corresponding to a specific day.
   * Toggles the enabled state of the day in the `hours` object. If the checkbox
   * is unchecked, the day is removed from the object. If the checkbox is checked,
   * the day is added with default start and end times.
   *
   * @param day - The name of the day (e.g., "Monday", "Tuesday") for which the checkbox state is being changed.
   */
  const handleCheckboxChange = (day: string) => {
    setHours((prevHours) => {
      const updatedHours = { ...prevHours };

      if (updatedHours[day]?.enabled) {
        // If the checkbox is unchecked, remove the day from the object
        delete updatedHours[day];
      } else {
        // If the checkbox is checked, add the day with default values
        updatedHours[day] = {
          enabled: true,
          day,
          start_time: "07:00",
          end_time: "18:00",
        };
      }

      return updatedHours;
    });
  };

  const [hours, setHours] = useState<
    Record<
      string,
      { enabled: boolean; day: String; start_time: string; end_time: string }
    >
  >(
    daysOfWeek.reduce((acc, loopDay) => {
      acc[loopDay] = {
        enabled: false,
        day: loopDay,
        start_time: "07:00",
        end_time: "18:00",
      };
      return acc;
    }, {} as Record<string, { enabled: boolean; day: String; start_time: string; end_time: string }>)
  );

  /**
   *
   * Parses a time string in the format "hh:mm AM/PM" and converts it to minutes.
   * @param time - The time string in the format "hh:mm AM/PM".
   * @returns
   */
  const parseTime = (time: string) => {
    const match = time.match(/(\d+):(\d+)\s(AM|PM)/);
    if (!match) return 0; // Return a default value if the match fails
    const [hour, minute, period] = match.slice(1);
    let h = parseInt(hour, 10);
    const m = parseInt(minute, 10);
    if (period === "PM" && h !== 12) h += 12;
    if (period === "AM" && h === 12) h = 0;
    return h * 60 + m;
  };

  /**
   * Filters the closing time options based on the selected opening time.
   * @param openTime - The opening time string in the format "hh:mm AM/PM".
   * @returns
   */
  const getFilteredCloseOptions = (openTime: string) => {
    return hoursOptions.filter(
      (time) => parseTime(time?.value) > parseTime(openTime)
    );
  };

  /**
   * Handles the change of time for a specific day and updates the hours state.
   *
   * @param day - The day for which the time is being updated (e.g., "Monday").
   * @param type - The type of time being updated, either "start_time" or "end_time".
   * @param value - The new time value to be set.
   *
   * @remarks
   * If the `type` is "start_time", the function automatically updates the `end_time`
   * to the first available option from `getFilteredCloseOptions` based on the new start time.
   * If no options are available, it defaults to the provided `value`.
   *
   * @example
   * ```typescript
   * handleTimeChange("Monday", "start_time", "09:00");
   * ```
   */
  const handleTimeChange = (
    day: string,
    type: "start_time" | "end_time",
    value: string
  ) => {
    const newHours = { ...hours[day], [type]: value };

    if (type === "start_time") {
      const filteredCloseOptions = getFilteredCloseOptions(value);
      newHours.end_time = filteredCloseOptions[0]?.value || value;
    } else if (
      type === "end_time" &&
      newHours.start_time &&
      value < newHours.start_time
    ) {
      // Prevent setting end_time before start_time
      toast.warn("End time cannot be before start time.");
      return;
    }

    setHours({
      ...hours,
      [day]: newHours,
    });
  };

  const handleRefresh = async () => {
    if (isAdmin && !showNotification) {
      navigate(AUTH_ROUTE.ADMIN_CENTERS);
    } else {
      await dispatch(getAllCenters());
    }
  };

  const handleAddressSuggestions = async (selectedKeyword: string) => {
    const response = await dispatch(getAddressSuggestionsThunk(selectedKeyword));
    
    if (isFulfilled(response)) {
      setAddressSuggestions(response.payload.data);
    } else if (searchInput.length === 0) {
      setAddressSuggestions([]);
    } else {
      setAddressSuggestions([]);
    }
  };

  const handleAddressChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const inputValue = e.target.value;
    setHasAddressSelected(false)
    setFieldValue("business_address", inputValue);
    if(inputValue.length > 2 && !hasAddressSelected){
      handleAddressSuggestions(inputValue);
    }
  };

  const handleSelectAddress = (selectedAddress: string) => {
    setFieldValue("business_address", selectedAddress);
    setAddressSuggestions([]); // Hide dropdown after selection
    setSelectedKeyword("")
    setHasAddressSelected(true)
  };

  /**
   * Custom hook using `useFormik` to manage the form state and validation for centre details.
   *
   * @constant
   * @property {Function} handleSubmit - Function to handle form submission.
   * @property {Object} values - Current values of the form fields.
   * @property {Object} errors - Validation errors for the form fields.
   * @property {Object} touched - Tracks which fields have been touched.
   * @property {Function} setFieldValue - Function to programmatically set the value of a specific field.
   * @property {Function} handleChange - Function to handle changes in form fields.
   *
   * @typedef {Object} InitialValues
   * @property {string} id - Centre ID.
   * @property {string} business_name - Name of the business.
   * @property {string} website - Website URL of the business.
   * @property {string} phone_number - Contact phone number.
   * @property {string} business_address - Address of the business.
   * @property {string} franchise - Franchise information.
   * @property {string} abn - Australian Business Number (ABN).
   * @property {string} placements - Placement details.
   * @property {string} profile - Profile information of the centre.
   * @property {string} expectations - Expectations of the centre.
   * @property {string} first_name - First name of the contact person.
   * @property {string} last_name - Last name of the contact person.
   * @property {string} email_address - Email address of the contact person.
   * @property {string} phoneno - Phone number of the contact person.
   * @property {string} role_id - Role ID of the contact person.
   * @property {Record<string, { enabled: boolean; day: string; open: string; close: string }>} timings - Operating timings of the centre.
   * @property {Array} transport - Transport options available.
   * @property {string} photo - Photo associated with the centre.
   *
   * @function onSubmit
   * @async
   * @param {InitialValues} values - Form values to be submitted.
   * @description Handles the form submission, dispatches actions to update centre details, and provides feedback to the user.
   * @throws {Error} Throws an error if the service connection fails.
   */
  const { handleSubmit, values, errors, touched, setFieldValue, handleChange } =
    useFormik({
      validationSchema: isAdmin
        ? createValidationSchema("adminCentreDetails")
        : createValidationSchema("centreDetails"),
      validateOnBlur: false,
      validateOnChange: false,
      initialValues: {
        id: "",
        business_name: "",
        website: "",
        phone: "",
        business_address: "",
        // franchise: "",
        abn: "",
        centre_invoice_email: "",
        placements: "",
        profile: "",
        expectations: "",
        first_name: "",
        last_name: "",
        email: "",
        user_phone: "",
        role_id: "",
        timings: {} as Record<
          string,
          { enabled: boolean; day: string; open: string; close: string }
        >,
        transport: [],
        photo: "", // Added photo field
      },
      onSubmit: async (values: CentreDetailsPayload) => {
        try {
          const payload = { ...values };
          let result;
          if (isAdmin && payload.id !== 0) {
            result = await dispatch(updateCentreDetailsThunk(payload));
          } else if (isAdmin) {
            result = await dispatch(createNewCentreThunk(payload));
          } else {
            result = await dispatch(updateCentreDetails(payload));
          }

          if (isFulfilled(result)) {
            dispatch(
              showNotificationAction(
                showBanner ? "Finished and Invite Sent" : "Saved"
              )
            );
            // setShowNotification(true)
            setTimeout(async () => {
              await handleRefresh();
            }, 3000);
          }
          localStorage.removeItem("centre_id");
        } catch (error) {
          throw new Error(
            "Could not connect to services at the moment. Please try again. Thanks."
          );
        }
      },
    });

  /**
   * Handles the display and population of centre details into the form fields.
   *
   * @param centreData - The data object representing a centre, containing all relevant details.
   *
   * This function performs the following actions:
   * - Sets the current centre ID.
   * - Populates various form fields with the centre's details, such as business name, website, phone number, address, franchise ID, ABN, placements, profile, and expectations.
   * - Updates the transport details by mapping the transport data into a specific format and setting it in the form.
   * - Updates the operating hours by transforming the timings data into a structured format and setting it in the form.
   */
  const handleShowCentreDetail = (centreData: TCenterDetails) => () => {
    setIsDisabled(false);
    setCurrentCentreId(centreData.id);
    setFieldValue(
      "business_name",
      centreData.business_name ? centreData.business_name : ""
    );
    setFieldValue("website", centreData.website ? centreData.website : "");
    setFieldValue(
      "phone",
      centreData.phone_number ? centreData.phone_number : ""
    );
    setFieldValue(
      "business_address",
      centreData.address ? centreData.address : ""
    );
    setFieldValue(
      "franchise",
      centreData.franchise_id ? centreData.franchise_id : ""
    );
    setFieldValue("abn", centreData.abn ? centreData.abn : "");
    setFieldValue(
      "placements",
      centreData.placements ? centreData.placements : ""
    );
    setFieldValue("photo", centreData?.photo ? centreData.photo : "");
    setFieldValue("profile", centreData.profile ? centreData.profile : "");
    setFieldValue(
      "expectations",
      centreData.expectations ? centreData.expectations : ""
    );

    // Update transport
    const updatedTransport = centreData.transport.map((transport) => ({
      mode: transport.transport_mode,
      parking_on_site: transport.parking_on_site ? 1 : 0,
      parking_on_surrounding: transport.parking_on_surrounding ? 1 : 0,
    }));
    setTransport(updatedTransport);
    setFieldValue("transport", updatedTransport);

    // Update timings
    const updatedHours = centreData.timings.reduce(
      (
        acc: Record<
          string,
          {
            enabled: boolean;
            day: string;
            start_time: string;
            end_time: string;
          }
        >,
        timing
      ) => {
        acc[timing.day] = {
          enabled: true,
          day: timing.day,
          start_time: timing.start_time,
          end_time: timing.end_time,
        };
        return acc;
      },
      {} as Record<
        string,
        { enabled: boolean; day: string; start_time: string; end_time: string }
      >
    );
    setHours(updatedHours);
    setFieldValue("centre_invoice_email", centreData.centre_invoice_email ? centreData.centre_invoice_email : "")
    setFieldValue("timings", updatedHours);
    sessionStorage.clear();
  };

  const redirectToCentreUsers = () => {
    navigate(AUTH_ROUTE.CLIENT_USERS);
  };

  /**
   * Checking if a specific mode of transport is selected in the transport state.
   * @param mode - The mode of transport to check.
   * @returns
   */
  const isTransportSelected = (mode: string) => {
    return transport.some((transport) => transport.mode === mode);
  };

  /**
   * Checking if parking is available on site for the selected mode of transport.
   * @returns
   */
  const isParkingAvailable = () => {
    return transport.some(
      (transport) => transport.mode === "car" && transport.parking_on_site
    );
  };

  /**
   * Checking if parking is available in the surrounding area for the selected mode of transport.
   * @returns
   **/
  const isParkingSurround = () => {
    return transport.some(
      (transport) =>
        transport.mode === "car" && transport.parking_on_surrounding
    );
  };

  const handleTrasportChange = (mode: string) => {
    setTransport((prevTrasnport) => {
      const updatedTransport = [...prevTrasnport];
      const existingIndex = updatedTransport.findIndex(
        (item) => item.mode === mode
      );
      if (existingIndex !== -1) {
        // If transport mode already exists, remove it
        updatedTransport.splice(existingIndex, 1);
      } else {
        // Add new transport mode
        updatedTransport.push({ mode });
      }
      return updatedTransport;
    });
  };

  /**
   * Updating the transport state with new values for a specific mode of transport.
   * @param prevTransport
   * @param mode
   * @param updates
   * @returns
   */
  const updateTransport = (
    prevTransport: Transport[],
    mode: string,
    updates: Partial<
      Pick<Transport, "parking_on_site" | "parking_on_surrounding">
    >
  ): Transport[] => {
    const updatedTransport = [...prevTransport];
    const existingIndex = updatedTransport.findIndex(
      (item) => item.mode === mode
    );

    if (existingIndex !== -1) {
      updatedTransport[existingIndex] = {
        ...updatedTransport[existingIndex],
        ...updates,
      };
    } else {
      updatedTransport.push({ mode, ...updates });
    }

    return updatedTransport;
  };

  const handleCarParkingTransportChange = (
    mode: string,
    parking: number = 0
  ): void => {
    setTransport((prevTransport: Transport[]) =>
      updateTransport(prevTransport, mode, { parking_on_site: parking })
    );
  };

  const handleCarParkingSurroundChange = (
    mode: string,
    parkingSurround: number = 0
  ) => {
    setTransport((prevTransport) =>
      updateTransport(prevTransport, mode, {
        parking_on_surrounding: parkingSurround,
      })
    );
  };

  /**
   * Getting all the centres when the component mounts.
   */
  useEffect(() => {
    const fetchCenters = async () => {
      dispatch(showTransparentPreloaderAction());
      if (location.pathname.includes("client")) {
        await dispatch(getAllCenters());
      } else if (location.pathname.includes("admin")) {
        // await dispatch(getAllActiveCentresThunk());
        if (
          localStorage.getItem("centre_id") !== null &&
          Number(localStorage.getItem("centre_id")) > 0
        ) {
          await dispatch(
            getCentreDetailsThunk({
              centre_id: Number(localStorage.getItem("centre_id")),
            })
          );
        }
      }
      if (!location.pathname.includes("create")) {
        await dispatch(
          getAllCentreUsers({
            page: 1,
            centre_id: [],
            role_id: [],
            per_page: DEFAULT_ITEMS_PER_PAGE,
          })
        );
      }
      dispatch(hideTransparentPreloaderAction());
    };

    // if(isCenterLoaded.current === true){
    //   isCenterLoaded.current = false;
    fetchCenters();
    // }
  }, [dispatch, isCenterLoaded, location, isCentreAdmin]);

  /**
   * Updating the transport and timings and photo values in the form state whenever they change.
   */
  useEffect(() => {
    setFieldValue("transport", transport);
    setFieldValue("timings", hours);

    setFieldValue(
      "id",
      currentCentreId !== null ? currentCentreId.toString() : 0
    );
  }, [
    transport,
    hours,
    profileImagePath,
    setFieldValue,
    isCentreUser,
    currentCentreId,
  ]);

  useEffect(() => {
    if (location.pathname.includes("admin") && profileImagePath) {
      setFieldValue("photo", profileImagePath);
    } else if (location.pathname.includes("client") && centreProfileImagePath) {
      setFieldValue("photo", centreProfileImagePath);
    }
  }, [setFieldValue, location, profileImagePath, centreProfileImagePath]);

  /**
   * Loading the centre details when the component mounts or when coming from the other pages.
   * Once particular centre loaded the centre_id is removed from the local storage.
   */
  useEffect(() => {
    let centreIdFromOtherPage = localStorage.getItem("centre_id");
    let centreDatatoLoad = null;
    if (allCentersList?.length > 0 && centreIdFromOtherPage !== null) {
      const centreId = centreIdFromOtherPage
        ? parseInt(centreIdFromOtherPage, 10)
        : null;
      centreDatatoLoad = centreId
        ? allCentersList.find(
            (centre: TAllCentersList) => centre.id === centreId
          )
        : allCentersList[0];

      localStorage.removeItem("centre_id");

      if (centreDatatoLoad && !location.pathname.includes("create")) {
        handleShowCentreDetail(centreDatatoLoad)();
      }
    } else {
      centreDatatoLoad = allCentersList[0];
      if (centreDatatoLoad && !location.pathname.includes("create")) {
        handleShowCentreDetail(centreDatatoLoad)();
      }
    }
  }, []); // Dependency array is empty for the solution of ACC-322

  /** this we used to set the values of admin centre details */
  useEffect(() => {
    if (
      centreDetails &&
      centreDetails !== null &&
      !location.pathname.includes("create") &&
      !isShowNotification
    ) {
      handleShowCentreDetail(centreDetails?.centre)();
    }
  }, [centreDetails, location, isShowNotification]);

  /**
   * Handles the click event for making a centre inactive.
   */
  /* const handleMakeInactive = async () => {
    const payload = new FormData();
    payload.append("centre_id", currentCentreId?.toString() || "");
    dispatch(showTransparentPreloaderAction());
    const centreStatus = await dispatch(updateCentreStatus(payload));
    dispatch(hideTransparentPreloaderAction());
    if (isFulfilled(centreStatus)) {
      toast.success("Centre status updated successfully");
    } else {
      toast.warn("Centre status updated failed");
    }
  }; */

  const handleRedirectSubmitEnquiry = () => {
    navigate(AUTH_ROUTE.CLIENT_ENQUIRY);
  };

  const redirectToEnquiry = () => {
    navigate(AUTH_ROUTE.ADMIN_CENTERS);
  };

  useEffect(() => {
    if (isAdmin && location.pathname.includes("create")) {
      if (
        values.business_name !== "" &&
        values.website !== "" &&
        values.phone !== "" &&
        values.business_address !== "" &&
        values.first_name !== "" &&
        values.last_name !== "" &&
        values.email !== "" &&
        values.user_phone !== ""
      ) {
        setIsDisabled(false);
      } else {
        setIsDisabled(true);
      }
    } else {
      setIsDisabled(false);
    }
  }, [values, isAdmin, location]);

  const filterTimesAfterStart = (
    times: { key: string; value: string }[],
    startTime: string
  ) => {
    return times.filter((time) => time.key > startTime);
  };

  return (
    <>
      {/* {showNotification && (
        <SaveNotification
          message={showBanner ? "Finished and Invite Sent" : "Saved"}
          onClose={() => setShowNotification(false)}
        />
      )} */}

      <div className="d-flex flex-column flex-lg-row gap-50 gap-mobile-20">
        <div className="d-flex flex-column gap-20 min-w-210px w-210px mobile-w-100 text-break">
          {isAdmin && (
            <CustomButton
              variant={"primary"}
              type="button"
              className="btn-theme bg-off-white text-black"
              onClick={redirectToEnquiry}
            >
              <LeftArrow />
              Back to All Centres
            </CustomButton>
          )}
          {!isAdmin && (
            <div className="fs-16 fw-700 text-teal-dark">
              {totalCentreUserCount} Centre Users{" "}
              <span
                className="fw-400 text-decoration-underline cursor-pointer"
                onClick={redirectToCentreUsers}
              >
                See All
              </span>
            </div>
          )}
          {!isAdmin ? (
            <>
              <div className="w-100 h-1px bg-off-white"></div>
              <div className="d-flex gap-10 align-items-center justify-content-between">
                <div className="fs-16 fw-700 text-teal-dark cursor-pointer">
                  {allCentersList.length} Centres
                </div>
                {isCentreAdmin && (
                  <div onClick={handleRedirectSubmitEnquiry}>
                    <RoundedPlus
                      className="min-w-24px cursor-pointer"
                      width={24}
                      height={24}
                      fill="#167E76"
                    />
                  </div>
                )}
              </div>
            </>
          ) : (
            ""
          )}
          {!isAdmin
            ? allCentersList.map((centreData: TCenterDetails) => (
                <div
                  key={centreData.id}
                  className={`fs-16 text-black cursor-pointer ${
                    centreData.id === currentCentreId && "fw-700"
                  }`}
                  onClick={handleShowCentreDetail(centreData)}
                >
                  {centreData.business_name}
                </div>
              ))
            : ""}
        </div>
        <div className="w-100 h-1px bg-off-white d-block d-lg-none"></div>
        <div className="border-left border-off-white pl-50px w-100 mobile-p-0 mobile-border-0">
          <div className="d-flex flex-column gap-20 max-w-650px">
            <div className="d-flex flex-column flex-lg-row gap-10 align-items-lg-center justify-content-lg-between w-100 text-break">
              <div className="fs-30 fw-400 text-black">
                {location.pathname.includes("create")
                  ? "Create a Centre"
                  : values.business_name}
              </div>
              <div className="d-flex gap-10 align-items-center text-nowrap">
                {!isCentreUser && (
                  <CustomButton
                    variant={"primary"}
                    isLoading={inProgress || superAdminInProgress}
                    contentClass="d-flex"
                    disabled={
                      isAdmin &&
                      location.pathname.includes("create") &&
                      isDisabled
                    }
                    type="button"
                    className="btn-theme bg-teal text-black mobile-w-100"
                    onClick={handleSubmit}
                  >
                    <CircleTick
                      fill={
                        isDisabled || inProgress || superAdminInProgress
                          ? "#aaaaaa"
                          : "#000000"
                      }
                    />
                    {isAdmin && location.pathname.includes("create")
                      ? "Finish Setup and Invite Admin"
                      : "Save"}
                  </CustomButton>
                )}
                {/* <CustomButton
                  variant={"primary"}
                  type="button"
                  className="btn-theme bg-off-white text-black"
                  onClick={handleMakeInactive}
                >
                  <CircleMinus />
                  Make Inactive
                </CustomButton> */}
              </div>
            </div>
            <div className="d-flex flex-column flex-md-row flex-wrap gap-30 align-items-center">
              <div className="d-flex flex-column order-2 order-md-1 gap-20">
                <ImageUpload
                  Apierror={error && error?.errors?.image?.[0]}
                  setUploadValidationError={setUploadValidationError}
                  type={!isAdmin ? "centre-image-client" : "centre-image-admin"}
                  centreId={currentCentreId}
                  previewImage={values.photo}
                />
              </div>
              <div className="w-100 order-3">
                {(errors || uploadValidationError) && (
                  <ErrorMessage
                    message={
                      (errors.photo as string) ||
                      (uploadValidationError as string)
                    }
                  />
                )}
              </div>
              {!isAdmin && !isCentreUser ? (
                <div className="bg-teal-light px-15px py-10px radius-8px d-flex gap-6 max-w-250px mobile-w-100 order-1 order-md-2">
                  <Warning />
                  <div className="fs-13 fw-400 text-black lh-sm">
                    Your profile picture is visible to candidates. A high
                    quality image is recommended.
                  </div>
                </div>
              ) : (
                ""
              )}
            </div>
            <div className="d-flex flex-column gap-10">
              <div className="d-flex flex-column gap-10">
                <div className="d-flex flex-column flex-lg-row gap-10">
                  <div className="flex-fill">
                    <TextInput
                      field={"business_name"}
                      value={values.business_name || ""}
                      label={isAdmin ? "Business Name*" : "Business Name"}
                      onChange={handleChange}
                      touched={touched.business_name! as boolean}
                      error={errors.business_name as string}
                      placeholder={" " + ""}
                      disabled={isCentreUser}
                    />
                  </div>
                  <div className="flex-fill">
                    <TextInput
                      field={"website"}
                      label={isAdmin ? "Website*" : "Website"}
                      value={values.website || ""}
                      onChange={handleChange}
                      touched={touched.website! as boolean}
                      error={errors.website as string}
                      placeholder={" "}
                      disabled={isCentreUser}
                    />
                  </div>
                </div>
                <div className="d-flex flex-column flex-lg-row gap-10">
                  <div className="w-180px mobile-w-100">
                    <TextInput
                      field={"phone"}
                      label={isAdmin ? "Phone Number*" : "Phone Number"}
                      onChange={handleChange}
                      value={values?.phone || ""}
                      touched={touched.phone! as boolean}
                      error={errors.phone as string}
                      placeholder={" " + ""}
                      disabled={isCentreUser}
                    />
                  </div>
                  <div className="flex-fill position-relative">
                    <TextInput
                      field={"business_address"}
                      label={isAdmin ? "Address*" : "Address"}
                      value={values?.business_address || ""}
                      onChange={handleAddressChange}
                      touched={touched.business_address! as boolean}
                      error={errors.business_address as string}
                      placeholder={" " + ""}
                      disabled={isCentreUser}
                    />
                    {/* Address Suggestions Dropdown */}
                    {addressSuggestions.length > 0 ? (
                      <div className="dropdown-shadow position-absolute bg-white z-1 border border-grey-light radius-8px overflow-hidden w-100">
                        {addressSuggestions.map((addr, index) => (
                          <div
                            key={index}
                            className="px-15px py-8px d-flex align-items-center cursor-pointer hover-bg-off-white text-break"
                            onClick={() =>
                              handleSelectAddress(addr.description)
                            }
                          >
                            {addr.description}
                          </div>
                        ))}
                      </div>
                    )
                    : (selectedKeyword.length > 0 && addressSuggestions.length === 0) ? 
                      <div className="dropdown-shadow position-absolute bg-white z-1 border border-grey-light radius-8px overflow-hidden w-100 py-10px pl-10px">No Address Found</div>
                    : "" }
                  </div>
                </div>
                <div className="d-flex flex-column flex-lg-row gap-10">
                  <div className="flex-fill">
                    <TextInput
                      field={"abn"}
                      label={"ABN"}
                      onChange={handleChange}
                      touched={touched.abn! as boolean}
                      error={errors.abn as string}
                      placeholder={" "}
                      value={values?.abn || ""}
                      disabled={isCentreUser}
                    />
                    {/* <TextInput
                      field={"franchise"}
                      label={"Franchise (Optional)"}
                      onChange={handleChange}
                      value={values?.franchise || ""}
                      touched={touched.franchise! as boolean}
                      error={errors.franchise as string}
                      placeholder={" " + ""}
                      disabled={isCentreUser}
                    /> */}
                  </div>
                  <div className="flex-fill">
                    <TextInput
                      field={"placements"}
                      label={"Licensed places"}
                      onChange={handleChange}
                      touched={touched.placements! as boolean}
                      error={errors.placements as string}
                      placeholder={" "}
                      value={values?.placements || ""}
                      disabled={isCentreUser}
                    />
                  </div>
                </div>
                <div className="d-flex flex-column flex-lg-row gap-10">
                  <div className="flex-fill">
                    <TextInput
                      field={"centre_invoice_email"}
                      label={"Email Address (To be used for invoicing)"}
                      onChange={handleChange}
                      touched={touched.centre_invoice_email! as boolean}
                      error={errors.centre_invoice_email as string}
                      placeholder={" "}
                      value={values?.centre_invoice_email || ""}
                      disabled={isCentreUser}
                    />
                    </div>
                  </div>
                {/* <div className="d-flex flex-column flex-lg-row gap-10">
                  <div className="w-100">
                    <TextInput
                      field={"placements"}
                      label={"Licensed places"}
                      onChange={handleChange}
                      touched={touched.placements! as boolean}
                      error={errors.placements as string}
                      placeholder={" "}
                      value={values?.placements || ""}
                      disabled={isCentreUser}
                    />
                  </div>
                  <div className="w-100"></div>
                </div> */}
              </div>
            </div>
            {isAdmin && location.pathname.includes("create") && (
              <div className="d-flex flex-column gap-20">
                <div className="fs-16 fw-400 text-black">
                  Invite primary contact to join as a Centre Admin:
                </div>
                <div className="d-flex flex-column gap-10">
                  <div className="d-flex flex-column flex-lg-row gap-10">
                    <div className="flex-fill">
                      <TextInput
                        field={"first_name"}
                        value={values.first_name || ""}
                        label={"First Name*"}
                        onChange={handleChange}
                        touched={touched.first_name! as boolean}
                        error={errors.first_name as string}
                        placeholder={" " + ""}
                      />
                    </div>
                    <div className="flex-fill">
                      <TextInput
                        field={"last_name"}
                        value={values.last_name || ""}
                        label={"Last Name*"}
                        onChange={handleChange}
                        touched={touched.last_name! as boolean}
                        error={errors.last_name as string}
                        placeholder={" "}
                      />
                    </div>
                  </div>
                  <div className="d-flex flex-column flex-lg-row gap-10">
                    <div className="flex-fill">
                      <TextInput
                        field={"email"}
                        value={values.email || ""}
                        label={"Email Address*"}
                        onChange={handleChange}
                        touched={touched.email! as boolean}
                        error={errors.email as string}
                        placeholder={" " + ""}
                      />
                      {superAdminError && (
                        <ErrorMessage
                          message={superAdminError?.errors?.email?.[0]}
                        />
                      )}
                    </div>
                    <div className="flex-fill">
                      <TextInput
                        field={"user_phone"}
                        value={values.user_phone || ""}
                        label={"Phone Number*"}
                        onChange={handleChange}
                        touched={touched.user_phone! as boolean}
                        error={errors.user_phone as string}
                        placeholder={" "}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div className="fs-22 fw-400 text-black">Opening Hours</div>
            <div className="d-flex flex-column gap-8">
              {daysOfWeek.map((day: string, index: number) => (
                <div
                  key={day}
                  className="d-flex gap-5 align-items-center flex-wrap flex-lg-nowrap"
                >
                  <div className="theme-check min-w-180px mobile-w-100">
                    <Checkbox
                      className="theme-check-input"
                      id={day}
                      checked={hours[day]?.enabled ? true : false}
                      onChange={() => handleCheckboxChange(day)}
                      label={day}
                      themeCheck={true}
                      labelBold={true}
                      disabled={isCentreUser}
                    />
                  </div>
                  {hours[day]?.enabled ? (
                    <>
                      <div className="form-group flex-fill">
                        <select
                          className="form-select"
                          defaultValue={hours[day].start_time}
                          onChange={(e) =>
                            handleTimeChange(day, "start_time", e.target.value)
                          }
                          disabled={isCentreUser}
                        >
                          {hoursOptions.map((time) => (
                            <option
                              key={time.key}
                              value={time.key}
                              selected={hours[day].start_time === time.key}
                            >
                              {time?.value}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="fs-16 fw-400 text-black">-</div>
                      <div className="form-group flex-fill">
                        <select
                          className="form-select"
                          value={hours[day].end_time}
                          defaultValue={hours[day].end_time}
                          onChange={(e) =>
                            handleTimeChange(day, "end_time", e.target.value)
                          }
                          disabled={isCentreUser}
                        >
                          {filterTimesAfterStart(
                            hoursOptions,
                            hours[day].start_time
                          ).map((time) => (
                            <option
                              key={time.key}
                              value={time.key}
                              selected={hours[day].end_time === time.key}
                            >
                              {time?.value}
                            </option>
                          ))}
                        </select>
                      </div>
                    </>
                  ) : (
                    <div className="flex-fill fs-16 fw-400 text-black lh-40px">
                      Closed
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="fs-22 fw-400 text-black">Centre Profile</div>
            <TextArea
              field={"profile"}
              // label={"Enter an introduction..."}
              placeholder={"Enter an introduction..."}
              onChange={handleChange}
              value={values?.profile || ""}
              disabled={isCentreUser}
              touched={touched.profile! as boolean}
              error={errors.profile as string}
            />
            <div className="fs-22 fw-400 text-black">Centre Expectations</div>
            <TextArea
              field={"expectations"}
              // label={"Enter details..."}
              placeholder={"Enter details..."}
              onChange={handleChange}
              value={values?.expectations || ""}
              disabled={isCentreUser}
              touched={touched.expectations! as boolean}
              error={errors.expectations as string}
            />
            <div className="fs-22 fw-400 text-black">Transport</div>
            <div className="fs-16 fw-400 text-black">
              Which mode/s of transport and parking locations are available
              nearby your centre?{" "}
            </div>
            <div className="d-flex flex-column gap-10">
              <div className="theme-check">
                <Checkbox
                  className="theme-check-input"
                  id="transport_car"
                  checked={isTransportSelected("car")}
                  onChange={(e) => {
                    handleTrasportChange("car");
                  }}
                  label={"Car"}
                  themeCheck={true}
                  disabled={isCentreUser}
                />
              </div>
              <div className="d-flex flex-column gap-10 pl-50px">
                <div className="theme-check">
                  <Checkbox
                    className="theme-check-input"
                    id="car_parking_site"
                    checked={isParkingAvailable()}
                    onChange={(checked: boolean) => {
                      handleCarParkingTransportChange("car", checked ? 1 : 0);
                    }}
                    label={"Parking is available on site"}
                    themeCheck={true}
                    disabled={isCentreUser}
                  />
                </div>
                <div className="theme-check">
                  <Checkbox
                    className="theme-check-input"
                    id="car_parking_surrounding"
                    checked={isParkingSurround()}
                    onChange={(checked: boolean) => {
                      handleCarParkingSurroundChange("car", checked ? 1 : 0);
                    }}
                    label={"Parking is available on surrounding streets"}
                    themeCheck={true}
                    disabled={isCentreUser}
                  />
                </div>
              </div>
              <div className="theme-check">
                <Checkbox
                  className="theme-check-input"
                  id="transport_bus"
                  checked={isTransportSelected("bus")}
                  onChange={(e) => {
                    handleTrasportChange("bus");
                  }}
                  label={"Bus"}
                  themeCheck={true}
                  disabled={isCentreUser}
                />
              </div>
              <div className="theme-check">
                <Checkbox
                  className="theme-check-input"
                  id="transport_train"
                  checked={isTransportSelected("train")}
                  onChange={(e) => {
                    handleTrasportChange("train");
                  }}
                  label={"Train"}
                  themeCheck={true}
                  disabled={isCentreUser}
                />
              </div>
              <div className="theme-check">
                <Checkbox
                  className="theme-check-input"
                  id="transport_tram"
                  checked={isTransportSelected("tram")}
                  onChange={(e) => {
                    handleTrasportChange("tram");
                  }}
                  label={"Tram"}
                  themeCheck={true}
                  disabled={isCentreUser}
                />
              </div>
            </div>
            <div className="fs-16 fw-400 text-black">
              Please only select the above public transportation if it is less
              than 15min walk away.
            </div>
            {/* <div className="d-flex justify-content-end pt-30px">
              <button type="button" className="btn-theme bg-teal text-black">
                <CircleTick />
                Saved!
              </button>
            </div> */}
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateCentres;
